import React from "react";
import { motion } from "framer-motion";
import { pageVariants } from "../animations/pageTransitions";
import "./Legals.css";

/**
 * Quantisys360 — Standardized API Documentation (ONC §170.315(g)(10)).
 * Publicly accessible developer documentation required as a Condition of Certification.
 * No registration, fee, or login is required to read this page.
 */

const preStyle = {
  background: "#0f172a",
  color: "#e2e8f0",
  padding: "14px 16px",
  borderRadius: "8px",
  overflowX: "auto",
  fontSize: "0.85rem",
  lineHeight: 1.5,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
};
const codeStyle = {
  background: "#eef2f7",
  padding: "1px 6px",
  borderRadius: "4px",
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  fontSize: "0.9em",
};

const Code = ({ children }) => <code style={codeStyle}>{children}</code>;
const Pre = ({ children }) => <pre style={preStyle}>{children}</pre>;

const ApiDocumentation = () => {
  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit">
      <div className="legal-container">
        <h1>Standardized API Documentation</h1>

        <p className="legal-note">
          This page is the publicly accessible developer documentation for the <strong>Quantisys360</strong>{" "}
          Standardized API, provided under ONC Health IT Certification criterion{" "}
          <strong>§170.315(g)(10) — Standardized API for Patient and Population Services</strong>. No
          registration, fee, login, or agreement is required to read this documentation.
        </p>

        <h2>Overview</h2>
        <div className="legal-highlight">
          <p>✔ HL7® FHIR® Release 4 (4.0.1), conforming to the US Core Implementation Guide</p>
          <p>✔ SMART App Launch (OAuth 2.0 + OpenID Connect) for patient- and user-facing apps</p>
          <p>✔ SMART Backend Services for system/population access and bulk export</p>
          <p>✔ Responses in <Code>application/fhir+json</Code> (and <Code>application/fhir+ndjson</Code> for bulk)</p>
        </div>
        <p className="legal-note">
          The API is multi-tenant and <strong>practice-scoped</strong>: every endpoint is namespaced under
          the identifier of the practice whose data an app is authorized to access. Clinical data is exposed
          read-only.
        </p>

        <h2>Service base URL</h2>
        <p>The production API host is <Code>https://synqare-api-108154680111.us-central1.run.app</Code>. The
          FHIR service base URL is per practice:</p>
        <Pre>{`https://synqare-api-108154680111.us-central1.run.app/practices/<practiceId>/fhir`}</Pre>
        <p className="legal-note">
          A machine-readable directory of every practice's service base URL (a FHIR Bundle of Endpoint
          resources) is publicly available at{" "}
          <Code>https://synqare-api-108154680111.us-central1.run.app/endpoints</Code>. The OAuth endpoints
          live under the sibling path <Code>.../practices/&lt;practiceId&gt;/oauth</Code>.
        </p>

        <h2>Discovery</h2>
        <p>Both discovery endpoints are public (no access token required):</p>
        <div className="legal-highlight">
          <p>✔ <Code>GET &lt;base&gt;/.well-known/smart-configuration</Code> — authorization, token,
            introspection, revocation, registration, and JWKS URLs; supported scopes, grant types, and
            SMART capabilities</p>
          <p>✔ <Code>GET &lt;base&gt;/metadata</Code> — the FHIR CapabilityStatement, including the SMART
            OAuth URIs and the supported resources and search parameters</p>
        </div>

        <h2>Scopes</h2>
        <p>
          SMART v1 scope syntax <Code>&lt;level&gt;/&lt;Resource&gt;.&lt;access&gt;</Code> plus context
          scopes. The full list is published in the discovery document. Common scopes:
        </p>
        <div className="legal-highlight">
          <p>✔ <Code>patient/*.read</Code> — read all of the launch patient's data</p>
          <p>✔ <Code>patient/Observation.read</Code> — read a specific resource type for the launch patient</p>
          <p>✔ <Code>user/*.read</Code> — read all data the authorizing user may access</p>
          <p>✔ <Code>system/*.read</Code> — population access for Backend Services / bulk export</p>
          <p>✔ <Code>launch/patient</Code>, <Code>openid</Code>, <Code>fhirUser</Code>, <Code>offline_access</Code></p>
        </div>
        <p className="legal-note">
          Supported resource types: Patient, Condition, AllergyIntolerance, MedicationRequest, Observation,
          Immunization, Procedure, Encounter, Provenance.
        </p>

        <h2>Registering an application</h2>
        <p>Apps register via the dynamic client registration endpoint:</p>
        <Pre>{`POST https://<service-host>/practices/<practiceId>/oauth/register
Content-Type: application/json

// Public app (PKCE):
{ "client_name": "My App", "redirect_uris": ["https://app.example/cb"],
  "token_endpoint_auth_method": "none",
  "scope": "patient/*.read openid offline_access" }`}</Pre>
        <p className="legal-note">
          Confidential apps use <Code>client_secret_basic</Code> (returns a client secret). Backend-services
          apps use <Code>private_key_jwt</Code> with a registered <Code>jwks</Code> or <Code>jwks_uri</Code>{" "}
          and do not need redirect URIs. In production, client registration may require approval — contact
          Quantisys360 to register a production client.
        </p>

        <h2>Authorization — SMART App Launch (patient &amp; provider apps)</h2>
        <p>Standalone authorization-code flow with PKCE:</p>
        <Pre>{`1) Authorize (browser redirect):
GET <oauth>/authorize?response_type=code&client_id=...&redirect_uri=...
    &scope=...&state=...&aud=<base>
    &code_challenge=<S256>&code_challenge_method=S256
   → user signs in and consents → redirect to <redirect_uri>?code=...&state=...

2) Token exchange:
POST <oauth>/token   (application/x-www-form-urlencoded)
grant_type=authorization_code&code=...&redirect_uri=...&client_id=...&code_verifier=...
   → { access_token, token_type: "Bearer", expires_in, scope, patient,
       id_token (openid), refresh_token (offline_access) }

3) Refresh:
grant_type=refresh_token&refresh_token=...&client_id=...`}</Pre>
        <p className="legal-note">
          Access tokens are signed JWTs; apps may verify them using the JWKS at <Code>GET &lt;oauth&gt;/jwks</Code>.
          A patient-scoped token can only access its own patient's data.
        </p>

        <h2>Authorization — SMART Backend Services (system / population)</h2>
        <p>For automated, population-level access using an asymmetric key:</p>
        <Pre>{`POST <oauth>/token   (application/x-www-form-urlencoded)
grant_type=client_credentials
&scope=system/*.read
&client_assertion_type=urn:ietf:params:oauth:client-assertion-type:jwt-bearer
&client_assertion=<JWT signed by your registered private key>
   → { access_token, token_type: "Bearer", expires_in, scope }`}</Pre>

        <h2>Reading data (FHIR interactions)</h2>
        <div className="legal-highlight">
          <p>✔ <Code>GET /Patient/&#123;id&#125;</Code> and <Code>GET /Patient?name|family|given|birthdate|gender|identifier|_id=...</Code></p>
          <p>✔ <Code>GET /&#123;Condition|AllergyIntolerance|MedicationRequest|Observation|Immunization|Procedure|Encounter&#125;?patient=&#123;id&#125;</Code></p>
          <p>✔ <Code>Observation</Code> supports <Code>?category=vital-signs|laboratory</Code></p>
          <p>✔ Searches support <Code>_count</Code> and <Code>_revinclude=Provenance:target</Code></p>
          <p>✔ <Code>GET /Patient/&#123;id&#125;/$everything</Code> — the patient plus all US Core resources and Provenance</p>
        </div>

        <h2>Bulk data export (population)</h2>
        <p>FHIR Bulk Data Access (Flat FHIR), using a <Code>system/*.read</Code> token:</p>
        <Pre>{`1) Kickoff:  GET <base>/Patient/$export   (or /$export, /Group/{id}/$export)
             → 202 Accepted, with a Content-Location status URL
2) Status:   GET <status-url>  → 202 (in progress) or 200 with a manifest of output files
3) Download: GET <output-url>  → application/fhir+ndjson (one resource per line)
4) Cancel:   DELETE <status-url>`}</Pre>

        <h2>Token introspection, revocation &amp; app management</h2>
        <div className="legal-highlight">
          <p>✔ <Code>POST &lt;oauth&gt;/introspect</Code> (RFC 7662) — check whether a token is active</p>
          <p>✔ <Code>POST &lt;oauth&gt;/revoke</Code> (RFC 7009) — revoke a token</p>
          <p>✔ Patients (or authorized staff) can review connected apps and revoke an app's access at any
            time; revocation takes effect immediately</p>
        </div>

        <h2>Standards &amp; conformance</h2>
        <p className="legal-note">
          The API implements FHIR R4, the US Core Implementation Guide, the SMART App Launch and SMART
          Backend Services Implementation Guides, OpenID Connect, and FHIR Bulk Data Access. Certification
          testing is performed using the ONC Inferno test kits for the Standardized API criterion.
        </p>

        <h2>Fees, terms, and limitations</h2>
        <p className="legal-note">
          Access to the Standardized API for reading a patient's own electronic health information is
          provided in accordance with the ONC API Condition of Certification and does not require special
          effort to access this documentation.
        </p>
        <div className="legal-highlight">
          <p>✔ Standard patient and application access via the certified API: no documentation or discovery fees</p>
          <p>✔ Production onboarding, high-volume, or custom integration services may be subject to
            commercially reasonable terms disclosed at registration</p>
          <p>✔ Use of the API is subject to applicable law, customer agreements, and Quantisys360's API terms of use</p>
        </div>

        <h2>Support</h2>
        <p className="legal-contact">
          For production client registration, service base URLs for specific instances, or developer
          support, contact{" "}
          <a href="mailto:contact@sshealthcareconsulting.com">contact@sshealthcareconsulting.com</a>.
        </p>
      </div>
    </motion.div>
  );
};

export default ApiDocumentation;
